import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Radio,
  RadioGroup,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Switch,
  TextField,
  Typography
} from '@mui/material';
import {
  ReceiptLong as ReceiptIcon,
  MarkEmailRead as EmailOkIcon,
  MailOutline as EmailIcon,
  AutoAwesome as AssistantIcon
} from '@mui/icons-material';
import { doc, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../firebase/config';
import brandingConfig from '../../config/brandingConfig';
import { getAssetPath } from '../../utils/assetManager';
import { useTransportWording } from '../../utils/transportWording';
import InvoiceSettingsSubsection from './InvoiceSettingsSection';

/**
 * Einstellungen → „Quittungen" — automatischer Quittungsversand für
 * App-Kunden (nextcab_mobile: docs/general/quittungsversand_konzept.md).
 *
 * Vier-Schritte-Assistent (Dialog): 1. Besteuerung (zuerst — Steuersatz UND
 * Treuerabatt-Behandlung hängen daran; die Verkehrsform kommt aus der
 * Unternehmensform unter „Betrieb & Disposition" und wird hier nur noch
 * angezeigt, siehe unternehmensform_konzept.md), 2. Unternehmensangaben
 * (Steuernummer/USt-IdNr Pflicht), 3. E-Mail-Versand (optional, Testmail),
 * 4. Zusammenfassung & Aktivieren. Die Aktivierung läuft über die Callable
 * `configureReceipts` und erzwingt serverseitig requireDriverFareInput = AN.
 */

// Anschrift aus dem Branding („Straße Nr, PLZ Ort") in Felder zerlegen.
const parseBrandingAddress = (address) => {
  const [street = '', rest = ''] = String(address || '').split(',').map((s) => s.trim());
  const match = rest.match(/^(\d{4,5})\s+(.+)$/);
  return {
    street,
    zip: match ? match[1] : '',
    city: match ? match[2] : rest
  };
};

const emptyCompany = () => {
  const parsed = parseBrandingAddress(brandingConfig.address);
  return {
    name: brandingConfig.companyName || '',
    owner: brandingConfig.owner || '',
    street: parsed.street,
    zip: parsed.zip,
    city: parsed.city,
    phone: brandingConfig.phone || '',
    email: brandingConfig.email || '',
    taxNumber: '',
    vatId: '',
    logo: null
  };
};

// Logo für die Quittung auf eine feste Box (Raster) herunterskalieren und als
// PNG-Data-URL zurückgeben — hält das Bild klein und druckt sauber. Das Seiten-
// verhältnis bleibt erhalten (fit-in-box), nie vergrößert.
const LOGO_MAX_W = 400;
const LOGO_MAX_H = 160;
const scaleImageToPngDataUrl = (src) => new Promise((resolve, reject) => {
  const img = new Image();
  img.onload = () => {
    const scale = Math.min(LOGO_MAX_W / img.width, LOGO_MAX_H / img.height, 1);
    const w = Math.max(1, Math.round(img.width * scale));
    const h = Math.max(1, Math.round(img.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, w, h);
    ctx.drawImage(img, 0, 0, w, h);
    try {
      resolve(canvas.toDataURL('image/png'));
    } catch (error) {
      reject(new Error('Das Bild konnte nicht verarbeitet werden.'));
    }
  };
  img.onerror = () => reject(new Error('Das Bild konnte nicht geladen werden.'));
  img.src = src;
});

const STEPS = ['Besteuerung', 'Unternehmensangaben', 'E-Mail-Versand', 'Rechnungen', 'Aktivieren'];

/** Untergruppen-Überschrift innerhalb eines Assistenten-Schritts. */
const StepGroup = ({ title, hint }) => (
  <Grid size={{ xs: 12 }}>
    <Stack direction="row" spacing={1} alignItems="baseline" sx={{ mt: 0.5 }}>
      <Typography variant="subtitle2">{title}</Typography>
      {hint && <Typography variant="caption" color="text.secondary">{hint}</Typography>}
    </Stack>
    <Divider sx={{ mt: 0.5 }} />
  </Grid>
);

const emptyInvoiceForm = () => ({
  numberPrefix: 'R-', monthlyRunDay: 1, defaultPaymentTermsDays: 14,
  iban: '', bic: '', holder: '', footerNote: ''
});

const ReceiptSettingsSection = ({
  onEnabledChange,
  onInvoicesEnabledChange,
  autoCompleteActive = false,
  canManage = true
}) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null); // { severity, message }

  // Verkehrsform = global eingestellte Unternehmensform (Einstellungen →
  // „Betrieb & Disposition"). Der Assistent zeigt sie nur noch an; die Cloud
  // Function `configureReceipts` liest sie ohnehin serverseitig.
  const { unternehmensform } = useTransportWording();

  // Live-Status aus appSettings/receipts
  const [enabled, setEnabled] = useState(false);
  const [emailEnabled, setEmailEnabled] = useState(false);
  const [storedCompany, setStoredCompany] = useState(null);
  const [storedTax, setStoredTax] = useState(null);
  const [smtpVerifiedAt, setSmtpVerifiedAt] = useState(null);
  const [smtpStored, setSmtpStored] = useState(null);

  // Assistent
  const [wizardOpen, setWizardOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [tax, setTax] = useState({ verkehrsform: unternehmensform, besteuerung: 'regel' });
  const [company, setCompany] = useState(emptyCompany);
  const [smtp, setSmtp] = useState({
    host: '', port: 587, user: '', password: '', secure: false,
    fromName: '', fromEmail: '', replyTo: ''
  });
  const [wantEmail, setWantEmail] = useState(true);
  const [testMailOk, setTestMailOk] = useState(false);
  const [testing, setTesting] = useState(false);
  const [wizardError, setWizardError] = useState(null);
  const [logoBusy, setLogoBusy] = useState(false);
  const [logoError, setLogoError] = useState(null);

  // Rechnungen (Firmenkunden) — teilt Firma/Steuer/SMTP mit den Quittungen
  // (rechnungskunden_konzept.md 4.6) und wird deshalb im selben Assistenten
  // mit eingerichtet. Gespeichert wird über die eigene Callable
  // `configureInvoices` nach appSettings/invoices.
  const [invStored, setInvStored] = useState(null);
  const [wantInvoices, setWantInvoices] = useState(false);
  const [inv, setInv] = useState(emptyInvoiceForm());

  useEffect(() => {
    const unsubscribe = onSnapshot(doc(db, 'appSettings', 'receipts'), (snapshot) => {
      const data = snapshot.exists() ? snapshot.data() : {};
      const isEnabled = data.receiptsEnabled === true;
      setEnabled(isEnabled);
      setEmailEnabled(data.emailEnabled === true);
      setStoredCompany(data.company || null);
      setStoredTax(data.tax || null);
      setSmtpStored(data.smtp || null);
      setSmtpVerifiedAt(data.smtp?.verifiedAt || null);
      setLoading(false);
      if (onEnabledChange) onEnabledChange(isEnabled);
    }, () => setLoading(false));
    return unsubscribe;
  }, [onEnabledChange]);

  useEffect(() => {
    const unsubscribe = onSnapshot(doc(db, 'appSettings', 'invoices'), (snapshot) => {
      setInvStored(snapshot.exists() ? snapshot.data() : {});
    }, () => {});
    return unsubscribe;
  }, []);

  const companyComplete = useMemo(() => (
    Boolean(storedCompany?.name && storedCompany?.owner && storedCompany?.street &&
      (storedCompany?.taxNumber || storedCompany?.vatId))
  ), [storedCompany]);

  const openWizard = () => {
    // Vorhandene Angaben übernehmen, sonst Branding-Vorbefüllung.
    setTax({
      // Verkehrsform immer aus der globalen Unternehmensform — nicht aus dem
      // Gespeicherten: sonst liefe eine spätere Umstellung auseinander.
      verkehrsform: unternehmensform,
      besteuerung: storedTax?.besteuerung === 'kleinunternehmer' ? 'kleinunternehmer' : 'regel'
    });
    setCompany({ ...emptyCompany(), ...(storedCompany || {}), taxNumber: storedCompany?.taxNumber || '', vatId: storedCompany?.vatId || '' });
    setSmtp({
      host: smtpStored?.host || '', port: smtpStored?.port || 587,
      user: smtpStored?.user || '', password: '',
      secure: smtpStored?.secure === true,
      fromName: smtpStored?.fromName || '', fromEmail: smtpStored?.fromEmail || '',
      replyTo: smtpStored?.replyTo || ''
    });
    setWantEmail(true);
    setTestMailOk(Boolean(smtpVerifiedAt));
    setWantInvoices(invStored?.invoicesEnabled === true);
    setInv({
      numberPrefix: invStored?.numberPrefix || 'R-',
      monthlyRunDay: invStored?.monthlyRunDay || 1,
      defaultPaymentTermsDays: invStored?.defaultPaymentTermsDays ?? 14,
      iban: invStored?.bank?.iban || '',
      bic: invStored?.bank?.bic || '',
      holder: invStored?.bank?.holder || '',
      footerNote: invStored?.footerNote || ''
    });
    setActiveStep(0);
    setWizardError(null);
    setWizardOpen(true);
  };

  const handleMainToggle = async () => {
    if (!enabled && !companyComplete) {
      // Erstaktivierung: erst der Assistent (Pflichtangaben inkl. Steuernummer).
      openWizard();
      return;
    }
    if (!enabled && autoCompleteActive) {
      // eslint-disable-next-line no-alert
      if (!window.confirm(
        'Für Quittungen werden echte Fahrpreise benötigt — „Fahrpreis nach App-Fahrt erfassen" wird mit aktiviert und gesperrt.\n\n' +
        'Hinweis: Der automatische Fahrtabschluss am Zielort entfällt dadurch für normale Fahrten.'
      )) return;
    } else if (!enabled) {
      // eslint-disable-next-line no-alert
      if (!window.confirm(
        'Für Quittungen werden echte Fahrpreise benötigt — „Fahrpreis nach App-Fahrt erfassen" wird mit aktiviert und gesperrt.'
      )) return;
    }
    setSaving(true);
    setFeedback(null);
    try {
      const callable = httpsCallable(functions, 'configureReceipts');
      await callable({ enabled: !enabled });
      setFeedback({
        severity: 'success',
        message: !enabled
          ? 'Quittungsversand aktiviert. Die Fahrpreis-Erfassung wurde automatisch mit eingeschaltet.'
          : 'Quittungsversand deaktiviert.'
      });
    } catch (error) {
      setFeedback({ severity: 'error', message: error?.message || 'Einstellung konnte nicht gespeichert werden.' });
    } finally {
      setSaving(false);
    }
  };

  // ── Assistent: Schritt-Validierung ──────────────────────────────────────
  const companyStepValid = Boolean(
    company.name.trim() && company.owner.trim() && company.street.trim() &&
    company.zip.trim() && company.city.trim() &&
    (company.taxNumber.trim() || company.vatId.trim())
  );
  const smtpFieldsFilled = Boolean(
    smtp.host.trim() && smtp.user.trim() && smtp.fromEmail.trim() &&
    (smtp.password.trim() || smtpVerifiedAt)
  );

  const handleTestMail = async () => {
    setTesting(true);
    setWizardError(null);
    try {
      const callable = httpsCallable(functions, 'sendReceiptTestEmail');
      const result = await callable({
        smtp: {
          host: smtp.host.trim(),
          port: Number(smtp.port) || 587,
          user: smtp.user.trim(),
          password: smtp.password,
          secure: smtp.secure,
          fromName: smtp.fromName.trim(),
          fromEmail: smtp.fromEmail.trim(),
          replyTo: smtp.replyTo.trim()
        }
      });
      setTestMailOk(true);
      setWizardError({
        severity: 'success',
        message: `Testmail an ${result.data?.to} verschickt — bitte Posteingang prüfen. Die Zugangsdaten wurden gespeichert.`
      });
    } catch (error) {
      setTestMailOk(false);
      setWizardError({ severity: 'error', message: error?.message || 'Testmail fehlgeschlagen.' });
    } finally {
      setTesting(false);
    }
  };

  const handleActivate = async () => {
    setSaving(true);
    setWizardError(null);
    try {
      const callable = httpsCallable(functions, 'configureReceipts');
      await callable({
        enabled: true,
        emailEnabled: wantEmail && testMailOk,
        company: {
          name: company.name.trim(),
          owner: company.owner.trim(),
          street: company.street.trim(),
          zip: company.zip.trim(),
          city: company.city.trim(),
          phone: company.phone.trim(),
          email: company.email.trim(),
          taxNumber: company.taxNumber.trim(),
          vatId: company.vatId.trim(),
          logo: company.logo || null
        },
        // Verkehrsform bewusst aus der globalen Unternehmensform; die Cloud
        // Function setzt sie ohnehin serverseitig aus appSettings/general.
        tax: { ...tax, verkehrsform: unternehmensform }
      });

      // Rechnungen (Firmenkunden): erst NACH configureReceipts speichern —
      // die Aktivierung prüft serverseitig genau diese geteilte Basis
      // (Firma + Steuernummer + geprüftes SMTP). Nur aufrufen, wenn sich
      // etwas ändert (Einschalten, Einstellungen bei aktivem Zustand,
      // Ausschalten) — sonst bleibt appSettings/invoices unberührt.
      const invoicesWereEnabled = invStored?.invoicesEnabled === true;
      if (wantInvoices || invoicesWereEnabled) {
        try {
          await httpsCallable(functions, 'configureInvoices')({
            enabled: wantInvoices,
            numberPrefix: inv.numberPrefix,
            monthlyRunDay: Number(inv.monthlyRunDay) || 1,
            defaultPaymentTermsDays: Number(inv.defaultPaymentTermsDays) || 14,
            bank: { iban: inv.iban, bic: inv.bic, holder: inv.holder },
            footerNote: inv.footerNote
          });
        } catch (invoiceError) {
          // Quittungen sind bereits aktiv — das Rechnungs-Problem gesondert
          // melden statt die ganze Einrichtung als gescheitert darzustellen.
          setWizardError({
            severity: 'warning',
            message: `Quittungen sind aktiv, aber die Rechnungs-Einrichtung schlug fehl: ${invoiceError?.message || invoiceError}`
          });
          setSaving(false);
          return;
        }
      }

      setWizardOpen(false);
      setFeedback({
        severity: 'success',
        message: wantInvoices
          ? 'Quittungsversand und Rechnungserstellung aktiviert. Die Fahrpreis-Erfassung wurde automatisch mit eingeschaltet.'
          : 'Quittungsversand aktiviert. Die Fahrpreis-Erfassung wurde automatisch mit eingeschaltet.'
      });
    } catch (error) {
      setWizardError({ severity: 'error', message: error?.message || 'Aktivierung fehlgeschlagen.' });
    } finally {
      setSaving(false);
    }
  };

  const setCompanyField = (field) => (event) => setCompany((prev) => ({ ...prev, [field]: event.target.value }));
  const setSmtpField = (field) => (event) => setSmtp((prev) => ({ ...prev, [field]: event.target.value }));
  const setInvField = (field) => (event) => setInv((prev) => ({ ...prev, [field]: event.target.value }));

  // Logo aus dem Branding übernehmen. Die logos-Schlüssel sind je Instanz
  // unterschiedlich belegt (mal ist das saubere Wortmarken-Logo unter 'icon',
  // mal unter 'primary'), daher zur Auswahl — die Vorschau zeigt das Ergebnis.
  // getAssetPath löst exakt wie die Navbar auf (PUBLIC_URL + logos[key]).
  const handleUseBrandingLogo = async (assetKey) => {
    setLogoError(null);
    setLogoBusy(true);
    try {
      const src = getAssetPath(assetKey, true);
      if (!src) throw new Error('Für diese Auswahl ist im Branding kein Logo hinterlegt.');
      const dataUrl = await scaleImageToPngDataUrl(src);
      setCompany((prev) => ({ ...prev, logo: dataUrl }));
    } catch (error) {
      setLogoError(error.message || 'Logo konnte nicht übernommen werden.');
    } finally {
      setLogoBusy(false);
    }
  };

  // Eigenes Logo hochladen (wird ebenfalls in die Box skaliert).
  const handleLogoUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setLogoError(null);
    setLogoBusy(true);
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(new Error('Datei konnte nicht gelesen werden.'));
        reader.readAsDataURL(file);
      });
      const scaled = await scaleImageToPngDataUrl(dataUrl);
      setCompany((prev) => ({ ...prev, logo: scaled }));
    } catch (error) {
      setLogoError(error.message || 'Logo konnte nicht geladen werden.');
    } finally {
      setLogoBusy(false);
    }
  };

  const verifiedDate = smtpVerifiedAt?.toDate ? smtpVerifiedAt.toDate().toLocaleDateString('de-DE') : null;

  // ── Assistent: Schritt-Inhalte ──────────────────────────────────────────
  const renderStep = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box>
            <Alert severity="info" icon={false} sx={{ mb: 2 }}>
              <strong>Verkehrsform:</strong>{' '}
              {unternehmensform === 'mietwagen'
                ? 'Mietwagenunternehmen (§ 49 PBefG)'
                : 'Taxiunternehmen (Taxi-Konzession, § 47 PBefG)'}
              <br />
              Übernommen aus der Unternehmensform unter{' '}
              <em>Einstellungen → Betrieb &amp; Disposition</em>. Dort ändern Sie sie,
              wenn nötig — die Angabe gilt auch für die Wortwahl in Portal und Apps.
            </Alert>
            <FormControl>
              <FormLabel>Besteuerung</FormLabel>
              <RadioGroup
                value={tax.besteuerung}
                onChange={(event) => setTax((prev) => ({ ...prev, besteuerung: event.target.value }))}
              >
                <FormControlLabel value="regel" control={<Radio />} label="Regelbesteuerung (USt wird ausgewiesen)" />
                <FormControlLabel value="kleinunternehmer" control={<Radio />} label="Kleinunternehmer § 19 UStG (kein USt-Ausweis)" />
              </RadioGroup>
            </FormControl>
            <Alert severity="info" sx={{ mt: 2 }}>
              <strong>Steuersatz:</strong> Taxi 7&nbsp;% bis 50&nbsp;km Strecke (auch bei Festpreis!),
              darüber 19&nbsp;% — Mietwagen immer 19&nbsp;%.
            </Alert>
          </Box>
        );
      case 1:
        return (
          <Grid container spacing={2}>
            <StepGroup title="Stammdaten" hint="erscheinen auf jeder Quittung und Rechnung" />
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth required size="small" label="Firmenname" value={company.name} onChange={setCompanyField('name')} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth required size="small" label="Inhaber" value={company.owner} onChange={setCompanyField('owner')} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth required size="small" label="Straße und Hausnummer" value={company.street} onChange={setCompanyField('street')} />
            </Grid>
            <Grid size={{ xs: 4, sm: 2 }}>
              <TextField fullWidth required size="small" label="PLZ" value={company.zip} onChange={setCompanyField('zip')} />
            </Grid>
            <Grid size={{ xs: 8, sm: 4 }}>
              <TextField fullWidth required size="small" label="Ort" value={company.city} onChange={setCompanyField('city')} />
            </Grid>

            <StepGroup title="Steuerangaben" hint="Steuernummer ODER USt-IdNr ist Pflicht (§ 14 UStG)" />
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth size="small" label="Steuernummer" value={company.taxNumber} onChange={setCompanyField('taxNumber')} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth size="small" label="USt-IdNr" placeholder="DE123456789" value={company.vatId} onChange={setCompanyField('vatId')} />
            </Grid>

            <StepGroup title="Kontakt für die Fußzeile" hint="optional" />
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth size="small" label="Telefon" value={company.phone} onChange={setCompanyField('phone')} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth size="small" type="email" label="E-Mail" value={company.email} onChange={setCompanyField('email')} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Alert severity="info">
                Die Angaben erscheinen auf jeder Quittung und Rechnung und
                speisen die Umsatzsteuer-Angabe im Impressum (Web und App). Sie
                gelten zentral für <strong>alle Fahrten dieser Instanz</strong> —
                auch bei mehreren Unternehmer-Konten.
              </Alert>
            </Grid>

            <StepGroup title="Logo" hint="optional — für Quittung und Rechnung" />
            <Grid size={{ xs: 12 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0.5, flexWrap: 'wrap' }}>
                {company.logo ? (
                  <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1, bgcolor: 'common.white' }}>
                    <img
                      src={company.logo}
                      alt="Quittungslogo"
                      style={{ maxHeight: 48, maxWidth: 180, display: 'block' }}
                    />
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Kein Logo — die Quittung zeigt nur den Firmennamen.
                  </Typography>
                )}
                <Button size="small" variant="outlined" onClick={() => handleUseBrandingLogo('icon')} disabled={logoBusy}>
                  Hauptlogo übernehmen
                </Button>
                <Button size="small" variant="outlined" onClick={() => handleUseBrandingLogo('primary')} disabled={logoBusy}>
                  Schriftzug übernehmen
                </Button>
                <Button size="small" variant="outlined" component="label" disabled={logoBusy}>
                  Hochladen
                  <input hidden type="file" accept="image/png,image/jpeg" onChange={handleLogoUpload} />
                </Button>
                {company.logo && (
                  <Button size="small" color="inherit" onClick={() => setCompany((prev) => ({ ...prev, logo: null }))} disabled={logoBusy}>
                    Entfernen
                  </Button>
                )}
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                Bitte die Vorschau prüfen und das saubere Firmenlogo wählen
                (Hauptlogo oder Schriftzug — je nach Branding).
              </Typography>
              {logoError && (
                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                  {logoError}
                </Typography>
              )}
            </Grid>
          </Grid>
        );
      case 2:
        return (
          <Box>
            <FormControlLabel
              control={<Switch checked={wantEmail} onChange={(event) => setWantEmail(event.target.checked)} />}
              label="Quittungen zusätzlich per E-Mail versenden"
            />
            {!wantEmail && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                Ohne E-Mail-Versand sind Quittungen nur in der Kunden-App abrufbar;
                Telefonkunden erhalten dann keine Quittungen.
              </Alert>
            )}
            {wantEmail && (
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid size={{ xs: 12, sm: 8 }}>
                  <TextField fullWidth required size="small" label="SMTP-Host" placeholder="smtp.ihre-domain.de" value={smtp.host} onChange={setSmtpField('host')} />
                </Grid>
                <Grid size={{ xs: 6, sm: 4 }}>
                  <TextField fullWidth required size="small" type="number" label="Port" value={smtp.port} onChange={setSmtpField('port')} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField fullWidth required size="small" label="Benutzername" value={smtp.user} onChange={setSmtpField('user')} autoComplete="off" />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField fullWidth required size="small" type="password" label="Passwort" value={smtp.password} onChange={setSmtpField('password')} autoComplete="new-password" helperText={smtpVerifiedAt ? 'Leer lassen = gespeichertes Passwort behalten' : 'Wird sicher im Secret Manager abgelegt'} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField fullWidth size="small" label="Absendername" placeholder={company.name} value={smtp.fromName} onChange={setSmtpField('fromName')} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField fullWidth required size="small" type="email" label="Absenderadresse" placeholder="quittung@ihre-domain.de" value={smtp.fromEmail} onChange={setSmtpField('fromEmail')} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField fullWidth size="small" type="email" label="Antwortadresse (optional)" value={smtp.replyTo} onChange={setSmtpField('replyTo')} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControlLabel
                    control={<Switch checked={smtp.secure} onChange={(event) => setSmtp((prev) => ({ ...prev, secure: event.target.checked }))} />}
                    label="TLS/SSL (Port 465)"
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Button
                    variant="outlined"
                    onClick={handleTestMail}
                    disabled={!smtpFieldsFilled || testing}
                    startIcon={testing ? <CircularProgress size={16} /> : <EmailIcon />}
                  >
                    {testing ? 'Testmail wird gesendet…' : 'Testmail senden'}
                  </Button>
                  {testMailOk && (
                    <Chip icon={<EmailOkIcon />} label="Versand geprüft" color="success" size="small" sx={{ ml: 1 }} />
                  )}
                  <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
                    Der E-Mail-Versand wird erst nach einer erfolgreichen Testmail aktivierbar.
                  </Typography>
                </Grid>
              </Grid>
            )}
          </Box>
        );
      case 3:
        // Rechnungen (Firmenkunden) — optional, teilt Firma/Steuer/SMTP mit
        // den Quittungen; nur das Rechnungsspezifische wird hier erfasst.
        return (
          <Box>
            <FormControlLabel
              control={
                <Switch
                  checked={wantInvoices}
                  onChange={(event) => setWantInvoices(event.target.checked)}
                />
              }
              label="Rechnungen für Firmenkunden erstellen (monatliche Sammelrechnung + Einzelrechnung)"
            />
            {!testMailOk && wantInvoices && (
              <Alert severity="info" sx={{ mt: 1 }}>
                Ohne geprüften E-Mail-Versand werden Rechnungen <strong>nicht
                automatisch versendet</strong> — sie stehen im Portal als PDF
                bereit und können nach der SMTP-Einrichtung per Knopfdruck
                versendet werden.
              </Alert>
            )}
            {wantInvoices && (
              <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
                Der automatische E-Mail-Versand ist zusätzlich <strong>pro
                Rechnungskunde</strong> einstellbar (Standard: aus — Rechnung
                erst kontrollieren, dann manuell versenden).
              </Typography>
            )}
            <Collapse in={wantInvoices}>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <StepGroup first title="Nummernkreis & Fristen" />
                <Grid size={{ xs: 6, sm: 4 }}>
                  <TextField fullWidth size="small" label="Rechnungsnummer-Präfix" placeholder="R-" value={inv.numberPrefix} onChange={setInvField('numberPrefix')} helperText={`z. B. „${inv.numberPrefix || 'R-'}2026-000001“`} />
                </Grid>
                <Grid size={{ xs: 6, sm: 4 }}>
                  <TextField fullWidth size="small" type="number" label="Monatslauf am Tag" value={inv.monthlyRunDay} onChange={setInvField('monthlyRunDay')} InputProps={{ inputProps: { min: 1, max: 28 } }} helperText="Sammelrechnungs-Lauf (1–28)" />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField fullWidth size="small" type="number" label="Zahlungsziel (Tage)" value={inv.defaultPaymentTermsDays} onChange={setInvField('defaultPaymentTermsDays')} InputProps={{ inputProps: { min: 0, max: 90 } }} helperText="Standard — je Kunde änderbar" />
                </Grid>

                <StepGroup title="Bankverbindung" hint="für den Zahlungshinweis auf der Rechnung" />
                <Grid size={{ xs: 12, sm: 5 }}>
                  <TextField fullWidth size="small" label="IBAN" placeholder="DE00 0000 0000 0000 0000 00" value={inv.iban} onChange={setInvField('iban')} />
                </Grid>
                <Grid size={{ xs: 12, sm: 3 }}>
                  <TextField fullWidth size="small" label="BIC" value={inv.bic} onChange={setInvField('bic')} />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField fullWidth size="small" label="Kontoinhaber" placeholder={company.name} value={inv.holder} onChange={setInvField('holder')} />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField fullWidth size="small" multiline minRows={2} label="Fußzeile / Zahlungshinweis (optional)" value={inv.footerNote} onChange={setInvField('footerNote')} />
                </Grid>
              </Grid>
            </Collapse>
          </Box>
        );
      default:
        return (
          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>{company.name}</strong> · {company.owner}<br />
              {company.street}, {company.zip} {company.city}<br />
              {company.vatId ? `USt-IdNr: ${company.vatId}` : `Steuernummer: ${company.taxNumber}`}<br />
              {unternehmensform === 'taxi' ? 'Taxiunternehmen' : 'Mietwagenunternehmen'} ·{' '}
              {tax.besteuerung === 'kleinunternehmer' ? 'Kleinunternehmer (§ 19 UStG)' : 'Regelbesteuerung'}<br />
              E-Mail-Versand: {wantEmail && testMailOk ? 'aktiv (Testmail erfolgreich)' : 'aus — Quittungen nur in der App'}<br />
              Rechnungen (Firmenkunden): {wantInvoices
                ? `aktiv — Monatslauf am ${Number(inv.monthlyRunDay) || 1}., Zahlungsziel ${Number(inv.defaultPaymentTermsDays) || 14} Tage`
                : 'aus — später über den Assistenten aktivierbar'}
            </Typography>
            <Alert severity="info" sx={{ mb: 1 }}>
              Mit der Aktivierung wird <strong>„Fahrpreis nach App-Fahrt erfassen"</strong> automatisch
              mit eingeschaltet und gesperrt — Quittungen enthalten nur echte, erfasste Fahrpreise.
              {autoCompleteActive && (
                <> Der automatische Fahrtabschluss am Zielort entfällt dadurch für normale Fahrten.</>
              )}
            </Alert>
          </Box>
        );
    }
  };

  const nextDisabled =
    (activeStep === 1 && !companyStepValid) ||
    (activeStep === 2 && wantEmail && !testMailOk);

  return (
    <>
      <Box sx={{ p: 2, bgcolor: 'grey.100' }}>
        <Typography variant="subtitle1" fontWeight="bold">
          <ReceiptIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          Quittungen &amp; Rechnungen
          {enabled && <Chip label="Quittungen aktiv" color="success" size="small" sx={{ ml: 1 }} />}
          {invStored?.invoicesEnabled === true && (
            <Chip label="Rechnungen aktiv" color="success" size="small" sx={{ ml: 1 }} />
          )}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Automatische PDF-Quittung nach jedem Fahrtabschluss (Kunden-App,
          optional per E-Mail) und Rechnungen für Firmenkunden — geteilte
          Basis: Firmen-Stammdaten, Steuer und SMTP werden einmal im
          Assistenten erfasst
        </Typography>
      </Box>

      {loading ? (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <>
          {!canManage && (
            <Alert severity="info" sx={{ mx: 3, my: 2 }}>
              Der Quittungsversand wird von der Hauptzentrale verwaltet.
            </Alert>
          )}
          <List disablePadding>
            <ListItem
              secondaryAction={
                <Switch
                  edge="end"
                  checked={enabled}
                  onChange={handleMainToggle}
                  disabled={!canManage || saving}
                  color="primary"
                />
              }
            >
              <ListItemIcon>
                <ReceiptIcon />
              </ListItemIcon>
              <ListItemText
                primary="Quittungen für App-Kunden erstellen"
                secondary={enabled
                  ? 'Nach jedem Fahrtabschluss mit erfasstem Fahrpreis wird automatisch eine Quittung erzeugt'
                  : companyComplete
                    ? 'Unternehmensangaben sind hinterlegt — Aktivierung schaltet die Fahrpreis-Erfassung mit ein'
                    : 'Die Einrichtung fragt Besteuerung, Unternehmensangaben (inkl. Steuernummer) und optional den E-Mail-Versand ab'}
              />
            </ListItem>
          </List>

          {/* Rechnungen (Firmenkunden) — Teil derselben Sektion: teilt
              Firma/Steuer/SMTP; Erst-Einrichtung über den Assistenten.
              Bewusst ÜBER dem E-Mail-Versand: der gilt für beides. */}
          <InvoiceSettingsSubsection
            canManage={canManage}
            onEnabledChange={onInvoicesEnabledChange}
          />

          <List disablePadding>
            <ListItem
              secondaryAction={
                <Button
                  variant="outlined"
                  startIcon={<AssistantIcon />}
                  onClick={openWizard}
                  disabled={!canManage}
                >
                  Assistent
                </Button>
              }
            >
              <ListItemIcon>
                {emailEnabled ? <EmailOkIcon color="success" /> : <EmailIcon />}
              </ListItemIcon>
              <ListItemText
                primary="E-Mail-Versand (Quittungen & Rechnungen)"
                secondary={emailEnabled
                  ? `eingerichtet${verifiedDate ? ` (zuletzt geprüft am ${verifiedDate})` : ''} — gemeinsames Postfach für Quittungen und Rechnungen`
                  : 'nicht eingerichtet — Quittungen sind nur in der Kunden-App abrufbar, Rechnungen nur im Portal'}
              />
            </ListItem>
          </List>

          {feedback && (
            <Alert severity={feedback.severity} sx={{ mx: 3, mb: 2 }} onClose={() => setFeedback(null)}>
              {feedback.message}
            </Alert>
          )}

          <Box sx={{ px: 3, py: 2, bgcolor: 'grey.50' }}>
            <Alert severity="info">
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Bei aktiven Quittungen ODER Rechnungen wird diese Einstellung
                automatisch festgelegt und gesperrt:
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                <li>„Fahrpreis nach App-Fahrt erfassen" — <strong>an</strong></li>
              </Box>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Quittungen und Rechnungen enthalten nur echte, erfasste
                Fahrpreise. Der automatische Fahrtabschluss am Zielort steht
                dadurch für normale Fahrten nicht zur Verfügung.
              </Typography>
            </Alert>
          </Box>
        </>
      )}

      {/* Einrichtungs-Assistent */}
      <Dialog open={wizardOpen} onClose={() => !saving && setWizardOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <AssistantIcon color="primary" />
            <Box>
              Quittungen &amp; Rechnungen einrichten
              <Typography variant="body2" color="text.secondary">
                Firmen-, Steuer- und Versanddaten gelten für beides — einmal erfassen, überall nutzen.
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
            {STEPS.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
          {renderStep()}
          {wizardError && (
            <Alert severity={wizardError.severity} sx={{ mt: 2 }} onClose={() => setWizardError(null)}>
              {wizardError.message}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWizardOpen(false)} disabled={saving}>Abbrechen</Button>
          <Box sx={{ flexGrow: 1 }} />
          {activeStep > 0 && (
            <Button onClick={() => setActiveStep((step) => step - 1)} disabled={saving}>Zurück</Button>
          )}
          {activeStep === 2 && wantEmail && !testMailOk && (
            <Button onClick={() => { setWantEmail(false); setActiveStep(3); }} disabled={saving}>
              Überspringen
            </Button>
          )}
          {activeStep < STEPS.length - 1 ? (
            <Button variant="contained" onClick={() => setActiveStep((step) => step + 1)} disabled={nextDisabled}>
              Weiter
            </Button>
          ) : (
            <Button variant="contained" onClick={handleActivate} disabled={saving}>
              {saving ? 'Wird aktiviert…' : 'Aktivieren'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ReceiptSettingsSection;
